import React, { Dispatch, SetStateAction } from 'react';
import clsx from 'clsx';
import { Button, Typography } from '@/components/common';
import { InputRangeDate, InputText } from '@/components/form';
import { FieldValues, useForm } from 'react-hook-form';
import { errorFieldMessage } from '@/const';
import { format, parseISO } from 'date-fns';
import { ModalHeader } from '@/components/dashboard';
import { casesLawyerService } from '@/api';
import { useNotification } from '@/hooks';
import useModal from '@/hooks/useModal';
import Icons from '@/const/icons';

const rules = {
	lawyerSignature: {
		required: { value: true, message: errorFieldMessage },
		pattern: {
			value: /^[A-Za-z\s]+$/i,
			message: 'Only alphabetic characters accepted',
		},
		maxLength: {
			value: 65,
			message: 'Your first name has to have less than 65 characters',
		},
		validate: (value: string) =>
			!!value?.trim() || 'Whitespace only is not allowed',
	},
	date: {
		required: { value: true, message: errorFieldMessage },
	},
};

interface Props {
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	isCaseReadOnly: boolean;
	caseId: string;
	handleApprove: (type: 'Retainer' | 'Document') => void;
}

export const RetainerContent: React.FC<Props> = ({
	isCaseReadOnly,
	caseId,
	open,
	setOpen,
	handleApprove,
}) => {
	const {
		Modal: ModalRetainer,
		show: showModalRetainer,
		hide: hideModalRetainer,
	} = useModal();
	const { toastError } = useNotification();

	const {
		watch,
		register,
		setValue,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting, isValid },
	} = useForm({ mode: 'onChange' });

	const lawyerSignature = watch('lawyerSignature');
	const currentDate = watch('date');
	const [date, setDate] = React.useState<{
		from: string | undefined;
		to: string | undefined;
	}>({
		from: undefined,
		to: undefined,
	});

	React.useEffect(() => {
		if (open) {
			showModalRetainer();
		}
	}, [open]);

	const handleSetDate = (
		name: string,
		date: { from: string | undefined; to: string | undefined } | undefined
	) => {
		if (date?.from && date?.to) {
			setValue(
				name,
				`${format(parseISO(date.from), 'MM/dd/yyyy')} - ${format(
					parseISO(date.to),
					'MM/dd/yyyy'
				)}`,
				{ shouldValidate: true, shouldDirty: true }
			);

			setDate({
				from: date.from,
				to: date.to,
			});
		}

		if (!date) {
			setDate({
				from: undefined,
				to: undefined,
			});
			setValue(name, undefined, { shouldValidate: true, shouldDirty: true });
		}
	};

	const handleCancel = () => {
		reset({});
		hideModalRetainer();
		setOpen(false);
	};

	const onSubmit = async (data: FieldValues) => {
		if (!caseId) return;

		try {
			await casesLawyerService.completeLawyerSign(
				data.lawyerSignature?.trim() || null,
				data.date || null,
				caseId
			);
			handleApprove('Retainer');
			handleCancel();
		} catch (error) {
			toastError('An error occurred while saving. Please try again.');
		}
	};

	const isDisabled =
		!currentDate || !lawyerSignature || !isValid || isSubmitting;
	return (
		<ModalRetainer
			withXButton
			avoidClose
			backgroundColor="white"
			classNameCardContainer="!w-[500px]"
			onClose={handleCancel}
			title={
				<ModalHeader
					icon={Icons.documentsEmptyState}
					title="Configure Retainer"
					classNameContent="!justify-start"
					classNameImage="w-[61px] h-12"
				/>
			}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="text-left space-y-6">
					<div className="flex-1">
						<Typography type="regular-16">Lawyer Signature</Typography>
						<InputText
							disabled={isCaseReadOnly}
							name="lawyerSignature"
							register={register}
							placeholder="Type your name here"
							classNameInput={clsx('w-full py-6 !rounded-3xl', {
								'!font-praise !text-4xl': !!lawyerSignature,
							})}
							rules={rules.lawyerSignature}
							error={errors?.lawyerSignature}
						/>
					</div>
					<div className="flex-1">
						<Typography type="regular-16">Preservation Date</Typography>
						<InputRangeDate
							disabled={isCaseReadOnly}
							restrictToPast
							name="date"
							isFormatted={false}
							register={register}
							dateFrom={date?.from}
							dateTo={date?.to}
							classNameTrigger="w-full 2xl:w-[404px]"
							classNameIcon="!top-[35%]"
							setValueInput={handleSetDate}
							rules={rules.date}
						/>
					</div>
				</div>
				<div className="mt-8 flex items-center gap-3 justify-end">
					<Button
						type="button"
						decoration="line-primary"
						onClick={handleCancel}
						size="fit"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						size="fit"
						decoration="primary"
						disabled={isDisabled}
					>
						Accept
					</Button>
				</div>
			</form>
		</ModalRetainer>
	);
};
